'use client'

import { useQuery } from '@apollo/client'
import { Box, Typography } from '@mui/material'
import { AspectRatio } from '@radix-ui/react-aspect-ratio'
import { usePathname } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import { Trans } from 'react-i18next'
import { GetVideoPreviewModalDocument, GetVideoPreviewModalQuery } from '~/__generated__/graphql'
import * as GA from '~/libs/ga'
import { Colors } from '~/libs/mui/theme'
import { PlayerBase } from './PlayerBase'
import { LogType } from './useLogger'

type Props = {
  id: string
}

export const PreviewPlayer = ({ id }: Props) => {
  const { data, loading } = useQuery<GetVideoPreviewModalQuery>(GetVideoPreviewModalDocument, {
    variables: { id },
  })
  const src = useMemo(() => data?.originalVideo?.trailer?.fileUrl || undefined, [data])
  const duration = useMemo(() => {
    const originDuration = data?.originalVideo?.trailer?.duration

    return (originDuration?.minutes || 0) * 60 + (originDuration?.seconds || 0)
  }, [data])
  const vtts = useMemo(() => data?.originalVideo?.trailer?.vtts || [], [data])
  const title = useMemo(() => data?.originalVideo?.title || '', [data])

  const asPath = usePathname()
  const screen = useMemo(() => {
    if (asPath === '/') {
      return 'stream'
    } else if (asPath?.startsWith('/search/categories')) {
      return 'category_videos'
    } else if (asPath?.startsWith('/search/result')) {
      return 'search_result'
    }
  }, [asPath])

  const sendTrailInsightsEvent = useCallback(
    (action: string, value?: string) => {
      if (screen === undefined) return
      GA.sendEvent({
        event: 'trail_insights',
        screen,
        category: 'trail',
        action,
        label: id,
        value,
      })
    },
    [screen, id],
  )

  const loggers = useMemo(
    (): { [key in LogType]?: (value?: string) => void } => ({
      mute: () => sendTrailInsightsEvent('video_views_mute_on'),
      unmute: () => sendTrailInsightsEvent('video_views_mute_off'),
      forward: () => sendTrailInsightsEvent('video_views_forward'),
      rewind: () => sendTrailInsightsEvent('video_views_back'),
      fullscreen: () => sendTrailInsightsEvent('video_views_fullscreen'),
      exitFullscreen: () => sendTrailInsightsEvent('video_views_shrink'),
      play: () => sendTrailInsightsEvent('video_views_play_count'),
      pause: () => sendTrailInsightsEvent('video_views_pause_count'),
      changeVolume: () => sendTrailInsightsEvent('video_views_volume_change'),
      changeSpeed: (value?: string) => sendTrailInsightsEvent('video_views_speed_value', value),
      watch1Sec: () => sendTrailInsightsEvent('video_views_time_watched_second'),
      watch3Sec: () => sendTrailInsightsEvent('video_views_3s_watched_count'),
      watch5Sec: () => sendTrailInsightsEvent('video_views_5s_watched_count'),
      watch10Sec: () => sendTrailInsightsEvent('video_views_10s_watched_count'),
      watch15Sec: () => sendTrailInsightsEvent('video_views_15s_watched_count'),
      watch30Sec: () => sendTrailInsightsEvent('video_views_30s_watched_count'),
      watchComplete: () => sendTrailInsightsEvent('video_views_complete_watched_count'),
    }),
    [sendTrailInsightsEvent],
  )

  if (!loading && !src) {
    return (
      <AspectRatio ratio={16 / 9}>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          width='100%'
          height='100%'
          bgcolor={Colors.color0D0D0E}
          borderRadius='8px'
        >
          <Typography>
            <Trans i18nKey='no_trailer' />
          </Typography>
        </Box>
      </AspectRatio>
    )
  }

  return (
    <PlayerBase
      title={title}
      src={src}
      duration={duration}
      vtts={vtts}
      hlsParameter=''
      loggers={loggers}
    />
  )
}
